import { useState, useEffect, useMemo, useRef } from 'react'
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts'
import { glorifyApiClient, GlorifyApiError } from '../../../services/api/glorify'
import type { MarketSearchResult } from '../../../services/api/glorify'
import { useSymbolSearch } from '../../../hooks/useSymbolSearch'
import { formatCurrency } from './marketsFormat'
import {
  computeSpreadSummary,
  payoffAtExpiry,
  defaultPriceRange,
  buildTemplateLegs,
  createEmptyLeg,
  plAtTime,
  positionDelta,
  positionGamma,
  positionTheta,
  buildDayOffsets,
  pnlMatrix,
  earliestExpiry,
  transformCell,
  isPercentMode,
  impliedVolFromPrice,
} from './optionsMath'
import type { OptionLeg, OptionAction, OptionType, SpreadTemplate, CellMode } from './optionsMath'

/** Everything the Payoff section renders from — frozen when Calculate is
 * pressed, so edits to the position or range never repopulate the chart/table
 * until the user recalculates. */
interface CalcSnapshot {
  legs: OptionLeg[]
  range: { min: number; max: number }
  underlyingPrice: number
  maxEvalDay: number
  summary: ReturnType<typeof computeSpreadSummary>
  signature: string
}

function calcSignature(legs: OptionLeg[], underlyingPrice: number, min: number, max: number): string {
  return JSON.stringify({ legs, underlyingPrice, min, max })
}

// ─── Chart theme — matches MarketsPage ──────────────────────────────────────

const md = {
  primary: 'var(--md-sys-color-primary)',
  tertiary: 'var(--md-sys-color-tertiary)',
  error: 'var(--md-sys-color-error)',
  onSurface: 'var(--md-sys-color-on-surface)',
  onSurfaceVariant: 'var(--md-sys-color-on-surface-variant)',
  outlineVariant: 'var(--md-sys-color-outline-variant)',
  surfaceContainerHigh: 'var(--md-sys-color-surface-container-high)',
}
const CHART_GRID = 'rgba(230, 225, 233, 0.08)'
const TOOLTIP_STYLE = {
  contentStyle: { background: md.surfaceContainerHigh, border: `1px solid ${md.outlineVariant}`, borderRadius: 8 },
  labelStyle: { color: md.onSurfaceVariant },
  itemStyle: { color: md.onSurface },
}

const TEMPLATE_LABELS: Record<SpreadTemplate, string> = {
  bull_call_spread: 'Bull Call Spread',
  bear_call_spread: 'Bear Call Spread',
  bull_put_spread: 'Bull Put Spread',
  bear_put_spread: 'Bear Put Spread',
  custom: 'Custom',
}

const CELL_MODE_LABELS: Record<Extract<CellMode, 'pl' | 'pctRisk'>, string> = {
  pl: 'P/L $',
  pctRisk: '% of risk',
}

const MAX_LEGS = 4
const MIN_LEGS = 2
const DEFAULT_UNDERLYING_PRICE = 100
const DEFAULT_EXPIRY_DAYS = 30
const DEFAULT_IV_PERCENT = 25
const DEFAULT_RATE_PERCENT = 4
const TABLE_ROWS = 17

type ViewMode = 'graph' | 'table'

/** A leg as edited in the UI — every numeric field is a raw STRING so a
 * field mid-typing (empty, "-", ".", etc.) never forces a re-render with
 * NaN. Parsed to a concrete `OptionLeg` (with the page-wide expiry/IV) via
 * `num()` right before it's handed to optionsMath.ts. */
interface LegInput {
  id: string
  action: OptionAction
  type: OptionType
  strike: string
  premium: string
  qty: string
}

// ─── NaN-proof numeric parsing ──────────────────────────────────────────────

/**
 * The ONLY numeric parsing path in this page. Blank/incomplete input
 * ('', '-', '.', '-.') or a non-finite result (NaN/Infinity) falls back to
 * `fallback` instead of ever propagating NaN into optionsMath.ts. Optionally
 * clamps to [min, max] once a real number is parsed.
 */
function num(raw: string, fallback: number, min?: number, max?: number): number {
  const trimmed = raw.trim()
  let value = fallback
  if (trimmed !== '' && trimmed !== '-' && trimmed !== '.' && trimmed !== '-.') {
    const parsed = Number(trimmed)
    if (Number.isFinite(parsed)) value = parsed
  }
  if (min !== undefined) value = Math.max(min, value)
  if (max !== undefined) value = Math.min(max, value)
  return value
}

/** Currency formatter that never prints "NaN" — non-finite input renders "—". */
function fmtCurrency(value: number, fractionDigits = 2): string {
  if (!Number.isFinite(value)) return '—'
  return formatCurrency(value, fractionDigits)
}

/** Percent formatter that never prints "NaN%" — non-finite input renders "—". */
function fmtPercent(value: number, fractionDigits = 1): string {
  if (!Number.isFinite(value)) return '—'
  return `${value.toFixed(fractionDigits)}%`
}

/** Plain fixed-point formatter (Greeks) — non-finite input renders "—". */
function fmtNum(value: number, fractionDigits: number): string {
  if (!Number.isFinite(value)) return '—'
  return value.toFixed(fractionDigits)
}

function fmtMoneyOrUnlimited(value: number | 'Unlimited'): string {
  if (value === 'Unlimited') return 'Unlimited'
  return fmtCurrency(Math.abs(value))
}

// ─── Date helpers (expiry <input type="date"> <-> days-from-today) ────────

function startOfToday(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

/** ISO yyyy-mm-dd for `days` from today. */
function isoDateFromDays(days: number): string {
  const d = startOfToday()
  d.setDate(d.getDate() + Math.max(0, Math.round(days)))
  return d.toISOString().slice(0, 10)
}

/**
 * Days from today for a yyyy-mm-dd string. An unparseable or past/today date
 * (the date input's `min` should prevent this, but a cleared field can still
 * produce one) falls back to `DEFAULT_EXPIRY_DAYS` rather than propagating
 * NaN or a non-positive DTE into the math layer.
 */
function daysFromIsoDate(iso: string): number {
  const target = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(target.getTime())) return DEFAULT_EXPIRY_DAYS
  const days = Math.round((target.getTime() - startOfToday().getTime()) / 86_400_000)
  return days >= 1 ? days : DEFAULT_EXPIRY_DAYS
}

/** "Jul 25" style label for `days` from today. */
function shortDateLabel(days: number): string {
  const d = startOfToday()
  d.setDate(d.getDate() + days)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const MIN_EXPIRY_ISO = isoDateFromDays(1)
const DEFAULT_EXPIRY_ISO = isoDateFromDays(DEFAULT_EXPIRY_DAYS)

// ─── Leg-scaffold helpers (thin wrappers around optionsMath's template/empty
// leg builders — we only reuse their id/strike/premium/action/type; expiry
// and IV are page-wide, not per-leg, here). ─────────────────────────────────

function legInputFromOptionLeg(o: OptionLeg): LegInput {
  return { id: o.id, action: o.action, type: o.type, strike: String(o.strike), premium: String(o.premium), qty: '1' }
}

function templateLegInputs(t: SpreadTemplate, underlyingPrice: number): LegInput[] {
  return buildTemplateLegs(t, underlyingPrice, DEFAULT_EXPIRY_DAYS, DEFAULT_IV_PERCENT / 100).map(legInputFromOptionLeg)
}

function blankLegInput(underlyingPrice: number): LegInput {
  return legInputFromOptionLeg(createEmptyLeg(underlyingPrice, DEFAULT_EXPIRY_DAYS, DEFAULT_IV_PERCENT / 100))
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function OptionsCalculatorPage(): React.JSX.Element {
  const [template, setTemplate] = useState<SpreadTemplate>('bull_call_spread')

  // ── Raw-string inputs — never parsed until the derived values below. ────
  const [underlyingPriceRaw, setUnderlyingPriceRaw] = useState(String(DEFAULT_UNDERLYING_PRICE))
  const [expiryIso, setExpiryIso] = useState(DEFAULT_EXPIRY_ISO)
  const [legInputs, setLegInputs] = useState<LegInput[]>(() =>
    templateLegInputs('bull_call_spread', DEFAULT_UNDERLYING_PRICE)
  )

  const [viewMode, setViewMode] = useState<ViewMode>('graph')
  const [cellMode, setCellMode] = useState<Extract<CellMode, 'pl' | 'pctRisk'>>('pl')
  const [evalDayRaw, setEvalDayRaw] = useState(0)

  // ── Parsed-with-fallback derived values — the ONLY numbers that ever
  // reach optionsMath.ts. See `num()` above for the fallback/clamp rules. ──
  const underlyingPrice = num(underlyingPriceRaw, DEFAULT_UNDERLYING_PRICE, 0.01)
  const expiryDays = daysFromIsoDate(expiryIso)
  // Risk-free rate: fixed internal constant, not a user input. Each leg's IV
  // is solved from its premium using this same rate, so it self-cancels at
  // T+0 and only mildly shapes mid-horizon values (~a few $ per contract).
  const riskFreeRate = DEFAULT_RATE_PERCENT / 100
  // Internal pricing fallback for legs whose premium can't imply a vol
  // (blank/zero premium mid-typing, or an arbitrage-violating value).
  // Deliberately not a user-facing input: real legs get their IV solved
  // from their entered premium.
  const iv = DEFAULT_IV_PERCENT / 100

  // Each leg is priced with the IV implied by its own entered premium, so
  // T+0 P&L is $0 at the current spot (you haven't won or lost anything the
  // moment you open the trade). The strategy IV field is only the fallback
  // for premiums that can't imply a vol (0, below intrinsic, etc.).
  const { legs, legIvs } = useMemo(() => {
    const tau = expiryDays / 365
    const ivs: { solved: number | null }[] = []
    const built: OptionLeg[] = legInputs.map((li) => {
      const strike = num(li.strike, DEFAULT_UNDERLYING_PRICE, 0.01)
      const premium = num(li.premium, 0, 0)
      const solved = impliedVolFromPrice(li.type, underlyingPrice, strike, tau, riskFreeRate, premium)
      ivs.push({ solved })
      return {
        id: li.id,
        action: li.action,
        type: li.type,
        strike,
        premium,
        contracts: Math.round(num(li.qty, 1, 1)),
        expiryDays,
        iv: solved ?? iv,
      }
    })
    return { legs: built, legIvs: ivs }
  }, [legInputs, expiryDays, iv, underlyingPrice, riskFreeRate])

  const maxEvalDay = useMemo(() => earliestExpiry(legs), [legs])

  // ── Calculation gate: the Payoff section renders from this snapshot only,
  // captured when the user presses Calculate. ─────────────────────────────
  const [calc, setCalc] = useState<CalcSnapshot | null>(null)
  const [calcError, setCalcError] = useState<string | null>(null)
  const [rangeMinRaw, setRangeMinRaw] = useState('')
  const [rangeMaxRaw, setRangeMaxRaw] = useState('')

  // Auto (strike-based) range — placeholder values for the blank inputs and
  // the fallback when a range field is left empty.
  const autoRange = useMemo(() => defaultPriceRange(legs), [legs])

  const handleCalculate = (): void => {
    const min = num(rangeMinRaw, autoRange.min, 0)
    const max = num(rangeMaxRaw, autoRange.max, 0.01)
    if (min >= max) {
      setCalcError('Range min must be less than range max.')
      return
    }
    setCalcError(null)
    setCalc({
      legs,
      range: { min, max },
      underlyingPrice,
      maxEvalDay,
      summary: computeSpreadSummary(legs),
      signature: calcSignature(legs, underlyingPrice, min, max),
    })
  }

  // Signature of what Calculate WOULD snapshot right now — when it differs
  // from the stored one, the rendered payoff is out of date.
  const currentSignature = useMemo(
    () =>
      calcSignature(
        legs,
        underlyingPrice,
        num(rangeMinRaw, autoRange.min, 0),
        num(rangeMaxRaw, autoRange.max, 0.01)
      ),
    [legs, underlyingPrice, rangeMinRaw, rangeMaxRaw, autoRange]
  )
  const calcStale = calc !== null && calc.signature !== currentSignature

  const sliderMax = calc?.maxEvalDay ?? maxEvalDay
  const evalDay = Math.min(evalDayRaw, sliderMax)
  const setEvalDay = (next: number): void => setEvalDayRaw(Math.max(0, Math.min(next, sliderMax)))
  // Greeks are live (not snapshotted), so they must never be evaluated past
  // the live position's earliest expiry.
  const greeksEvalDay = Math.min(evalDay, maxEvalDay)

  // ── Optional symbol link, for prefilling the underlying price ─────────
  const [symbolQuery, setSymbolQuery] = useState('')
  const [showSymbolDropdown, setShowSymbolDropdown] = useState(false)
  const symbolSearch = useSymbolSearch(symbolQuery)
  const symbolBoxRef = useRef<HTMLDivElement>(null)
  const [linkedSymbol, setLinkedSymbol] = useState<string | null>(null)
  const [priceLoading, setPriceLoading] = useState(false)
  const [priceError, setPriceError] = useState<string | null>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent): void {
      if (symbolBoxRef.current && !symbolBoxRef.current.contains(e.target as Node)) {
        setShowSymbolDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // ── Template selection regenerates legs around the current underlying;
  // Custom preserves whatever legs are already there. ─────────────────────
  const handleTemplateChange = (next: SpreadTemplate): void => {
    setTemplate(next)
    if (next !== 'custom') {
      setLegInputs(templateLegInputs(next, underlyingPrice))
    } else if (legInputs.length === 0) {
      setLegInputs([blankLegInput(underlyingPrice), blankLegInput(underlyingPrice)])
    }
  }

  const handleAddLeg = (): void => {
    if (legInputs.length >= MAX_LEGS) return
    setLegInputs((prev) => [...prev, blankLegInput(underlyingPrice)])
  }

  const handleRemoveLeg = (id: string): void => {
    if (legInputs.length <= MIN_LEGS) return
    setLegInputs((prev) => prev.filter((li) => li.id !== id))
  }

  const updateLegInput = (id: string, patch: Partial<Omit<LegInput, 'id'>>): void => {
    setLegInputs((prev) => prev.map((li) => (li.id === id ? { ...li, ...patch } : li)))
  }

  const handleSelectSymbol = (result: MarketSearchResult): void => {
    setLinkedSymbol(result.symbol)
    setSymbolQuery('')
    setShowSymbolDropdown(false)
    setPriceError(null)
  }

  const handleUseLivePrice = async (): Promise<void> => {
    if (!linkedSymbol) return
    setPriceLoading(true)
    setPriceError(null)
    try {
      const quote = await glorifyApiClient.marketsQuote(linkedSymbol)
      if (Number.isFinite(quote.price) && quote.price > 0) {
        setUnderlyingPriceRaw(String(quote.price))
      } else {
        setPriceError(`Received an invalid price for ${linkedSymbol}`)
      }
    } catch (err) {
      setPriceError(
        err instanceof GlorifyApiError ? err.message : `Failed to fetch a live price for ${linkedSymbol}`
      )
    } finally {
      setPriceLoading(false)
    }
  }

  // ── Derived math (all pure, from optionsMath.ts) ───────────────────────
  // Summary cards stay live; everything under "Payoff" derives from `calc`.
  const summary = useMemo(() => computeSpreadSummary(legs), [legs])
  const payoffPoints = useMemo(
    () => (calc ? payoffAtExpiry(calc.legs, { ...calc.range, steps: 150 }) : []),
    [calc]
  )

  const chartData = useMemo(
    () =>
      calc
        ? payoffPoints.map((p) => ({
            price: p.price,
            profit: p.profit,
            tPlusN: plAtTime(calc.legs, p.price, evalDay, riskFreeRate),
          }))
        : [],
    [payoffPoints, calc, evalDay, riskFreeRate]
  )

  // Gradient split point so the area fill is green above $0 P/L, red below —
  // computed from the actual data domain so the color boundary lines up
  // exactly with the y=0 reference line regardless of chart auto-scaling.
  const { dataMin, dataMax, gradientOffset } = useMemo(() => {
    const values = chartData.flatMap((p) => [p.profit, p.tPlusN])
    const max = Math.max(0, ...values)
    const min = Math.min(0, ...values)
    const offset = max === min ? 0.5 : max / (max - min)
    return { dataMin: min, dataMax: max, gradientOffset: offset }
  }, [chartData])

  // ── Greeks at the current underlying price and selected evaluation day ──
  const posDelta = useMemo(() => positionDelta(legs, underlyingPrice, greeksEvalDay, riskFreeRate), [legs, underlyingPrice, greeksEvalDay, riskFreeRate])
  const posGamma = useMemo(() => positionGamma(legs, underlyingPrice, greeksEvalDay, riskFreeRate), [legs, underlyingPrice, greeksEvalDay, riskFreeRate])
  const posTheta = useMemo(() => positionTheta(legs, underlyingPrice, greeksEvalDay, riskFreeRate), [legs, underlyingPrice, greeksEvalDay, riskFreeRate])

  // ── P&L matrix: price rows (descending) spanning the calculated range ×
  // densified evaluation-day columns (ending at the earliest leg expiry). ──
  const priceGridDesc = useMemo(() => {
    if (!calc) return []
    const { min, max } = calc.range
    const step = (max - min) / (TABLE_ROWS - 1)
    return Array.from({ length: TABLE_ROWS }, (_, i) => max - i * step)
  }, [calc])
  const dayOffsets = useMemo(() => buildDayOffsets(calc?.maxEvalDay ?? maxEvalDay), [calc, maxEvalDay])
  const matrixRows = useMemo(
    () => (calc ? pnlMatrix(calc.legs, priceGridDesc, dayOffsets, riskFreeRate) : []),
    [calc, priceGridDesc, dayOffsets, riskFreeRate]
  )

  const closestRowIndex = useMemo(() => {
    if (!calc || matrixRows.length === 0) return -1
    let closest = 0
    let smallestDiff = Infinity
    matrixRows.forEach((row, i) => {
      const diff = Math.abs(row.price - calc.underlyingPrice)
      if (diff < smallestDiff) {
        smallestDiff = diff
        closest = i
      }
    })
    return closest
  }, [matrixRows, calc])

  // Column headers: real calendar dates for each day offset, "Exp" for the
  // last (expiry) column.
  const columnDates = useMemo(
    () => dayOffsets.map((offset, i) => (i === dayOffsets.length - 1 ? 'Exp' : shortDateLabel(offset))),
    [dayOffsets]
  )
  const expiryDateLabel = useMemo(() => shortDateLabel(calc?.maxEvalDay ?? maxEvalDay), [calc, maxEvalDay])

  // Scale for the cell colour gradient — largest absolute raw $ P&L across
  // the whole matrix, so intensity is relative to this strategy's own range.
  const matrixMaxAbs = useMemo(() => {
    let max = 0
    matrixRows.forEach((row) => row.cells.forEach((c) => { max = Math.max(max, Math.abs(c.pl)) }))
    return max || 1
  }, [matrixRows])

  function cellStyle(plDollars: number): React.CSSProperties {
    if (!Number.isFinite(plDollars) || Math.abs(plDollars) < matrixMaxAbs * 0.01) {
      return { background: 'transparent' }
    }
    const alpha = 0.15 + 0.65 * (Math.abs(plDollars) / matrixMaxAbs)
    const color = plDollars >= 0 ? `rgba(74, 222, 128, ${alpha})` : `rgba(248, 113, 113, ${alpha})`
    return { background: color }
  }

  /** Compact P&L cell text: no $ sign, 1 dp under 100, 0 dp otherwise, "k" for thousands. */
  function formatCellValue(value: number): string {
    if (!Number.isFinite(value)) return '—'
    const abs = Math.abs(value)
    const sign = value < 0 ? '-' : ''
    if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}k`
    if (abs < 100) return `${sign}${abs.toFixed(1)}`
    return `${sign}${abs.toFixed(0)}`
  }

  function cellDisplay(pl: number): string {
    const s = calc?.summary ?? summary
    const transformed = transformCell(pl, cellMode, s.entryCost, s.maxLoss, 0)
    if (cellMode === 'pctRisk' && isPercentMode(cellMode, s.entryCost, s.maxLoss)) {
      return Number.isFinite(transformed) ? `${transformed.toFixed(1)}%` : '—'
    }
    return formatCellValue(transformed)
  }

  const daysLeft = Math.max(sliderMax - evalDay, 0)

  return (
    <>
      <div className="gd-page-header">
        <h1>Options Spread Calculator</h1>
        <p>
          Model the payoff for a multi-leg options position at a single strategy-wide IV and
          expiry. Everything below runs locally in your browser — no market data is required
          unless you link a symbol for live pricing.
        </p>
      </div>

      {/* ── Setup ─────────────────────────────────────────────────────── */}
      <div className="gd-section">
        <h2 className="gd-section__title">Setup</h2>
        <div className="gd-setup-row">
          <div className="gd-field">
            <label htmlFor="gd-template">Strategy</label>
            <select
              id="gd-template"
              className="gd-select"
              value={template}
              onChange={(e) => handleTemplateChange(e.target.value as SpreadTemplate)}
            >
              {(Object.keys(TEMPLATE_LABELS) as SpreadTemplate[]).map((key) => (
                <option key={key} value={key}>{TEMPLATE_LABELS[key]}</option>
              ))}
            </select>
          </div>

          <div className="gd-field">
            <label htmlFor="gd-underlying-price">Underlying price</label>
            <input
              id="gd-underlying-price"
              type="number"
              step="0.01"
              min="0.01"
              value={underlyingPriceRaw}
              onChange={(e) => setUnderlyingPriceRaw(e.target.value)}
            />
          </div>

          <div className="gd-search" ref={symbolBoxRef} style={{ maxWidth: 260 }}>
            <div className="gd-search__input-wrap">
              <span className="material-icons" aria-hidden="true">search</span>
              <input
                className="gd-search__input"
                type="text"
                placeholder="Link a symbol…"
                value={symbolQuery}
                onChange={(e) => {
                  setSymbolQuery(e.target.value)
                  setShowSymbolDropdown(true)
                }}
                onFocus={() => setShowSymbolDropdown(true)}
                aria-label="Search for a symbol to link"
              />
            </div>
            {showSymbolDropdown && symbolQuery.trim().length > 0 && (
              <div className="gd-search__dropdown">
                {symbolSearch.isLoading ? (
                  <div className="gd-search__empty">Searching…</div>
                ) : symbolSearch.error ? (
                  <div className="gd-search__empty">{symbolSearch.error}</div>
                ) : symbolSearch.results.length === 0 ? (
                  <div className="gd-search__empty">No matches.</div>
                ) : (
                  symbolSearch.results.map((r) => (
                    <button
                      key={`${r.symbol}-${r.exchange}`}
                      className="gd-search__item"
                      onClick={() => handleSelectSymbol(r)}
                    >
                      <span><b>{r.symbol}</b> — {r.name}</span>
                      <small>{r.exchange_full_name || r.exchange}</small>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {linkedSymbol && (
            <button
              className="gd-btn gd-btn--outlined has-icon"
              onClick={() => void handleUseLivePrice()}
              disabled={priceLoading}
            >
              <span className="material-icons" aria-hidden="true">bolt</span>
              {priceLoading ? 'Loading…' : `Use live price (${linkedSymbol})`}
            </button>
          )}

          <div className="gd-field">
            <label htmlFor="gd-expiry">Expiry</label>
            <input
              id="gd-expiry"
              type="date"
              min={MIN_EXPIRY_ISO}
              value={expiryIso}
              onChange={(e) => setExpiryIso(e.target.value)}
            />
            <div className="gd-field-hint gd-cell-muted">({expiryDays}d)</div>
          </div>

        </div>

        {priceError && (
          <div className="gd-banner gd-banner--error" role="alert">
            <span className="material-icons" aria-hidden="true">warning</span>
            <p>{priceError}</p>
          </div>
        )}
      </div>

      {/* ── Legs editor ──────────────────────────────────────────────── */}
      <div className="gd-section">
        <div className="gd-section__head">
          <h2 className="gd-section__title" style={{ marginBottom: 0 }}>Legs</h2>
          {template === 'custom' && (
            <button className="gd-btn gd-btn--tonal has-icon" onClick={handleAddLeg} disabled={legInputs.length >= MAX_LEGS}>
              <span className="material-icons" aria-hidden="true">add</span>
              Add leg
            </button>
          )}
        </div>

        <div className="gd-table-wrap">
          <table className="gd-table gd-legs-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Type</th>
                <th>Strike</th>
                <th>Premium</th>
                <th>Qty</th>
                <th>Total</th>
                {template === 'custom' && <th aria-label="Remove" />}
              </tr>
            </thead>
            <tbody>
              {legInputs.map((li, legIdx) => {
                const premium = num(li.premium, 0, 0)
                const solvedIv = legIvs[legIdx]?.solved ?? null
                const qty = Math.round(num(li.qty, 1, 1))
                const total = premium * 100 * qty
                const isDebit = li.action === 'buy'
                return (
                  <tr key={li.id}>
                    <td>
                      <select
                        value={li.action}
                        onChange={(e) => updateLegInput(li.id, { action: e.target.value as OptionAction })}
                        aria-label="Action"
                      >
                        <option value="buy">Buy</option>
                        <option value="sell">Sell</option>
                      </select>
                    </td>
                    <td>
                      <select
                        value={li.type}
                        onChange={(e) => updateLegInput(li.id, { type: e.target.value as OptionType })}
                        aria-label="Option type"
                      >
                        <option value="call">Call</option>
                        <option value="put">Put</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.5"
                        min="0.01"
                        value={li.strike}
                        onChange={(e) => updateLegInput(li.id, { strike: e.target.value })}
                        aria-label="Strike price"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={li.premium}
                        onChange={(e) => updateLegInput(li.id, { premium: e.target.value })}
                        aria-label="Premium per share"
                      />
                      <div className="gd-leg-iv-hint" title="Implied volatility solved from this premium; drives this leg's pricing">
                        {solvedIv !== null ? `IV ${(solvedIv * 100).toFixed(1)}%` : 'IV — enter a premium'}
                      </div>
                    </td>
                    <td>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={li.qty}
                        onChange={(e) => updateLegInput(li.id, { qty: e.target.value })}
                        aria-label="Contracts"
                      />
                    </td>
                    <td className="gd-leg-total" style={{ color: isDebit ? 'var(--md-sys-color-error)' : 'var(--md-sys-color-tertiary)' }}>
                      {isDebit ? '-' : '+'}{fmtCurrency(total)}
                    </td>
                    {template === 'custom' && (
                      <td>
                        <button
                          className="gd-btn gd-btn--icon"
                          onClick={() => handleRemoveLeg(li.id)}
                          disabled={legInputs.length <= MIN_LEGS}
                          aria-label="Remove leg"
                          title="Remove leg"
                        >
                          <span className="material-icons" aria-hidden="true">delete</span>
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Estimated returns ────────────────────────────────────────── */}
      {typeof summary.maxProfit === 'number' && summary.maxProfit <= 0 && (
        <div className="gd-banner gd-banner--error" role="alert">
          <span className="material-icons" aria-hidden="true">warning</span>
          <span>
            This position cannot profit at any expiry price. Check the premiums — for the same expiry, a
            lower-strike call (or higher-strike put) should cost more than the one it&apos;s paired with.
          </span>
        </div>
      )}
      <div className="gd-summary-row">
        {[
          { label: summary.entryCost >= 0 ? 'Net Debit' : 'Net Credit', value: fmtCurrency(Math.abs(summary.entryCost)) },
          {
            label: 'Max Profit',
            value:
              summary.maxProfit === 'Unlimited'
                ? 'Unlimited'
                : `${summary.maxProfit < 0 ? '-' : ''}${fmtCurrency(Math.abs(summary.maxProfit))}`,
            color: typeof summary.maxProfit === 'number' && summary.maxProfit < 0 ? md.error : md.tertiary,
          },
          { label: 'Max Loss', value: fmtMoneyOrUnlimited(summary.maxLoss), color: md.error },
          { label: 'Return on Risk', value: summary.returnOnRisk === null ? '—' : fmtPercent(summary.returnOnRisk * 100, 1) },
          {
            label: `Breakeven${summary.breakevens.length > 1 ? 's' : ''}`,
            value: summary.breakevens.length === 0 ? '—' : summary.breakevens.map((b) => fmtCurrency(b)).join(' / '),
          },
          { label: 'Delta (Δ)', value: fmtNum(posDelta, 2) },
          { label: 'Gamma (Γ)', value: fmtNum(posGamma, 4) },
          { label: 'Theta (Θ) / day', value: fmtNum(posTheta, 2), color: Number.isFinite(posTheta) && posTheta < 0 ? md.error : undefined },
        ].map((card) => (
          <div className="gd-card gd-card--elevated" key={card.label}>
            <div className="gd-summary-card__label">{card.label}</div>
            <div className="gd-summary-card__value" style={card.color ? { color: card.color } : undefined}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      {/* ── Payoff / P&L: graph or table ─────────────────────────────── */}
      <div className="gd-section">
        <div className="gd-section__head">
          <h2 className="gd-section__title" style={{ marginBottom: 0 }}>Payoff</h2>
          <div className="gd-segmented" role="group" aria-label="Graph or table view">
            {(['graph', 'table'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                className={`gd-segmented__btn${viewMode === mode ? ' gd-segmented__btn--active' : ''}`}
                onClick={() => setViewMode(mode)}
              >
                {mode === 'graph' ? 'Graph' : 'Table'}
              </button>
            ))}
          </div>
        </div>

        {/* ── Calculation range + Calculate button ─────────────────────── */}
        <div className="gd-setup-row" style={{ marginBottom: 12 }}>
          <div className="gd-field">
            <label htmlFor="gd-range-min">Price range min</label>
            <input
              id="gd-range-min"
              type="number"
              step="0.01"
              min="0"
              placeholder={autoRange.min.toFixed(2)}
              value={rangeMinRaw}
              onChange={(e) => setRangeMinRaw(e.target.value)}
            />
            <div className="gd-field-hint gd-cell-muted">Blank = auto</div>
          </div>
          <div className="gd-field">
            <label htmlFor="gd-range-max">Price range max</label>
            <input
              id="gd-range-max"
              type="number"
              step="0.01"
              min="0.01"
              placeholder={autoRange.max.toFixed(2)}
              value={rangeMaxRaw}
              onChange={(e) => setRangeMaxRaw(e.target.value)}
            />
            <div className="gd-field-hint gd-cell-muted">Blank = auto</div>
          </div>
          <button className="gd-btn gd-btn--filled has-icon" onClick={handleCalculate}>
            <span className="material-icons" aria-hidden="true">calculate</span>
            Calculate
          </button>
        </div>

        {calcError && (
          <div className="gd-banner gd-banner--error" role="alert">
            <span className="material-icons" aria-hidden="true">warning</span>
            <p>{calcError}</p>
          </div>
        )}

        {calc === null ? (
          <div className="gd-empty">
            <span className="material-icons" aria-hidden="true">calculate</span>
            <p>Set a price range (or leave blank for the automatic strike-based range) and press Calculate to build the payoff.</p>
          </div>
        ) : (
          <>
            {calcStale && (
              <div className="gd-banner gd-banner--info" role="status" style={{ marginBottom: 12 }}>
                <span className="material-icons" aria-hidden="true">update</span>
                <p>Inputs have changed since the last calculation — press Calculate to refresh the payoff.</p>
              </div>
            )}

            <div className="gd-eval-slider">
              <label htmlFor="gd-eval-day">
                Evaluate at: T+{evalDay} ({daysLeft}d left)
              </label>
              <input
                id="gd-eval-day"
                type="range"
                min={0}
                max={sliderMax}
                step={1}
                value={evalDay}
                onChange={(e) => setEvalDay(num(e.target.value, evalDay))}
                aria-label="Evaluation day"
              />
            </div>

            {viewMode === 'graph' ? (
          <div className="gd-chart-card">
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={chartData} margin={{ left: 0, right: 16, top: 24 }}>
                <defs>
                  <linearGradient id="gd-payoff-split" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={gradientOffset} stopColor={md.tertiary} stopOpacity={0.35} />
                    <stop offset={gradientOffset} stopColor={md.error} stopOpacity={0.35} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={CHART_GRID} vertical={false} />
                <XAxis
                  dataKey="price"
                  type="number"
                  domain={[calc.range.min, calc.range.max]}
                  tickFormatter={(v: number) => formatCurrency(v, 0)}
                  stroke={md.onSurfaceVariant}
                  fontSize={11}
                />
                <YAxis
                  domain={[dataMin, dataMax]}
                  tickFormatter={(v: number) => formatCurrency(v, 0)}
                  stroke={md.onSurfaceVariant}
                  fontSize={11}
                  width={72}
                />
                <Tooltip
                  {...TOOLTIP_STYLE}
                  labelFormatter={(v) => `Underlying ${formatCurrency(Number(v))}`}
                  formatter={(value, name) => [formatCurrency(Number(value)), name === 'profit' ? 'At expiry' : 'T+n']}
                />
                <Legend
                  formatter={(value) => (value === 'profit' ? 'At expiry' : `T+${evalDay}`)}
                  wrapperStyle={{ fontSize: 12, color: md.onSurfaceVariant }}
                />
                <ReferenceLine y={0} stroke={md.outlineVariant} />
                {calc.summary.breakevens.map((b) => (
                  <ReferenceLine
                    key={b}
                    x={b}
                    stroke={md.onSurfaceVariant}
                    strokeDasharray="4 4"
                    label={{ value: `BE ${formatCurrency(b)}`, position: 'insideTopLeft', fill: md.onSurfaceVariant, fontSize: 10 }}
                  />
                ))}
                <ReferenceLine
                  x={calc.underlyingPrice}
                  stroke={md.primary}
                  strokeDasharray="4 4"
                  label={{ value: 'Current', position: 'insideTopRight', fill: md.primary, fontSize: 10 }}
                />
                <Area
                  type="monotone"
                  dataKey="profit"
                  name="profit"
                  stroke={md.primary}
                  strokeWidth={2}
                  fill="url(#gd-payoff-split)"
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="tPlusN"
                  name="tPlusN"
                  stroke="#80D5CE"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <>
            <div className="gd-matrix-controls">
              <div className="gd-segmented" role="group" aria-label="Matrix output mode">
                {(Object.keys(CELL_MODE_LABELS) as Array<Extract<CellMode, 'pl' | 'pctRisk'>>).map((mode) => (
                  <button
                    key={mode}
                    className={`gd-segmented__btn${cellMode === mode ? ' gd-segmented__btn--active' : ''}`}
                    onClick={() => setCellMode(mode)}
                  >
                    {CELL_MODE_LABELS[mode]}
                  </button>
                ))}
              </div>
            </div>

            <div className="gd-table-wrap">
              <table className="gd-table gd-pnl-matrix">
                <thead>
                  <tr>
                    <th className="gd-pnl-matrix__price-head">{linkedSymbol ?? 'Price'}</th>
                    {columnDates.map((label, i) => (
                      <th
                        key={dayOffsets[i]}
                        className="gd-pnl-matrix__date-head"
                        title={label === 'Exp' ? `Expires ${expiryDateLabel}` : undefined}
                      >
                        {label === 'Exp' ? <span className="gd-pnl-matrix__exp-chip">Exp</span> : label}
                      </th>
                    ))}
                    <th className="gd-pnl-matrix__pct-head">+/-%</th>
                  </tr>
                </thead>
                <tbody>
                  {matrixRows.map((row, i) => (
                    <tr key={row.price} className={i === closestRowIndex ? 'gd-row--current' : undefined}>
                      <td className="gd-pnl-matrix__price-cell">{row.price.toFixed(2)}</td>
                      {row.cells.map((cell, j) => (
                        <td key={dayOffsets[j]} className="gd-pnl-matrix__cell" style={cellStyle(cell.pl)}>
                          {cellDisplay(cell.pl)}
                        </td>
                      ))}
                      <td className="gd-pnl-matrix__pct-cell">
                        {calc.underlyingPrice > 0
                          ? `${(((row.price - calc.underlyingPrice) / calc.underlyingPrice) * 100).toFixed(2)}%`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
            )}
          </>
        )}
      </div>
    </>
  )
}
